import React, { useCallback, useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, InfoIcon, Text, TextVariant } from '@mprolab/uikit'
import { Download, Key, Shield, Terminal as TerminalIcon, TrendingUp } from 'react-feather'
import { Link } from 'react-router-dom'
import MainWrapper from 'layouts/MainWrapper'
import { StyledBiennale } from 'components'
import { FEATURES } from './features'
import { RELEASE_NOTES } from './notes'
import {
  PLATFORM_LABEL,
  ReleaseState,
  TerminalDownload,
  detectPlatform,
  downloadUrl,
  fetchRelease,
  formatSize,
  groupForVisitor
} from './releases'

/**
 * The Terminal download page (documentation/specification/TERMINAL.md).
 *
 * Reached from the user menu. Its whole job is to say what Terminal is, who
 * may run it, and to hand over a build — and, while no build is published, to
 * say that plainly instead of offering a button that fails.
 */

const Column = styled(Flex)`
  width: 100%;
  flex-direction: column;
  margin: 152px 0 120px;
`

const Header = styled(Flex)`
  align-items: center;
  gap: 12px;
`

const StyledTitle = styled(StyledBiennale)`
  font-size: 32px;
`

const Lead = styled(Text)`
  max-width: 720px;
  margin-top: 12px;
  color: ${({ theme }) => theme.colors.gray3};
`

const Cards = styled(Flex)`
  width: 100%;
  gap: 16px;
  margin-top: 32px;
  flex-wrap: wrap;
`

const Card = styled(Flex)`
  flex: 1 1 260px;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.background};
`

const CardHead = styled(Flex)`
  align-items: center;
  gap: 8px;
`

const DownloadSection = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 12px;
  margin-top: 40px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  border-radius: 12px;
`

const Buttons = styled(Flex)`
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
`

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.purple};
  background: ${({ theme }) => theme.colors.purple};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  text-decoration: none;
`

const Retry = styled.button`
  align-self: flex-start;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  font-size: 13px;
  cursor: pointer;
`

const Note = styled(Text)`
  color: ${({ theme }) => theme.colors.gray3};
`

/*
 * The platform's gradient, the one Terminal draws its download bar with.
 *
 * The operator asked for it by pointing at that bar — "the tiles should have a
 * coloured border like the loading bar in the version module, because it looks
 * great". Same four stops, same order, so the page and the application are
 * recognisably one product rather than two that share a logo.
 */
const BRAND_GRADIENT = 'linear-gradient(120deg, #FF029A, #9239FF, #3CA9FF, #03F5FF)'

const SectionHead = styled(Flex)`
  width: 100%;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin: 56px 0 4px;
`

const Tiles = styled.div`
  width: 100%;
  display: grid;
  gap: 14px;
  margin-top: 20px;
  /*
   * Three across, counted rather than fitted.
   *
   * Fitting to a 260px minimum put four on a wide screen, and there are
   * nine tiles: four, four, one. A row holding a single tile reads as a
   * mistake. Three divides nine exactly, so the count is pinned and only the
   * breakpoints move it down.
   */
  grid-template-columns: repeat(3, 1fr);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

/*
 * A gradient border, drawn as a border rather than faked with a shadow.
 *
 * Two backgrounds: the solid one clipped to the padding box, the gradient one
 * clipped to the border box. That leaves exactly the one pixel of border
 * showing the gradient, at any corner radius, without a wrapper element and
 * without the inner box having to know the page's colour twice.
 */
const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border: 1px solid transparent;
  border-radius: 14px;
  background:
    linear-gradient(${({ theme }) => theme.colors.background}, ${({ theme }) => theme.colors.background})
      padding-box,
    ${BRAND_GRADIENT} border-box;
`

const TileName = styled(Text)`
  font-size: 15px;
  font-weight: 700;
`

const Table = styled.div`
  width: 100%;
  margin-top: 20px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  border-radius: 12px;
  overflow: hidden;
`

const Row = styled(Link)`
  display: flex;
  align-items: baseline;
  gap: 18px;
  padding: 14px 18px;
  text-decoration: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray8};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundActive};
  }
`

const Version = styled(Text)`
  flex: 0 0 64px;
  font-family: monospace;
  color: ${({ theme }) => theme.colors.cyan};
`

const Headline = styled(Text)`
  flex: 1;
  color: ${({ theme }) => theme.colors.white};
`

const More = styled(Link)`
  color: ${({ theme }) => theme.colors.cyan};
  text-decoration: none;
  font-size: 14px;
  white-space: nowrap;
`

const InlineLink = styled(Link)`
  color: ${({ theme }) => theme.colors.cyan};
`

const Downloads: React.FC<{ state: ReleaseState; onRetry: () => void }> = ({ state, onRetry }) => {
  if (state.status === 'loading') {
    return <Note variant={TextVariant.BODY_SMALL}>Checking for a published build…</Note>
  }

  if (state.status === 'unreadable') {
    /*
     * We could not ask. That is not "there is no build" — saying so would be
     * the failure this platform keeps paying for: a value we could not read,
     * rendered as an answer.
     */
    return (
      <>
        <Note variant={TextVariant.BODY_SMALL}>
          We could not check for a build right now ({state.reason}). Nothing is wrong with your
          licence — this page could not reach the platform.
        </Note>
        <Retry onClick={onRetry}>Try again</Retry>
      </>
    )
  }

  if (state.status === 'none') {
    return (
      <Note variant={TextVariant.BODY_SMALL}>
        No build has been published yet. This page will offer them for macOS, Windows and Linux
        the moment they exist.
      </Note>
    )
  }

  const { release } = state
  const { mine, others } = groupForVisitor(release.downloads, detectPlatform())
  return (
    <>
      {/*
        The version, said plainly and where it is looked for.

        A person comes here for one of two reasons: to install Terminal, or to
        find out whether the copy they already run is behind. The second reason
        needs a number they can compare, and it needs to say which number it is
        — "Version 0.3.4" alone does not tell you it is the newest one.
      */}
      <Note variant={TextVariant.BODY_SMALL}>
        <strong>Latest version: {release.version}</strong>
        {release.publishedAt
          ? ` · published ${new Date(release.publishedAt).toLocaleDateString()}`
          : ''}
      </Note>
      <Note variant={TextVariant.BODY_SMALL}>
        Terminal shows the version it runs in Settings, and updates itself when a
        newer one is published.
      </Note>
      {/* The build for the system this visitor is on, first and on its own. */}
      <Buttons>{(mine.length > 0 ? mine : others).map(renderDownload)}</Buttons>
      {mine.length > 0 && others.length > 0 ? (
        <>
          <Note variant={TextVariant.BODY_SMALL}>Other systems</Note>
          <Buttons>{others.map(renderDownload)}</Buttons>
        </>
      ) : null}
      {release.notes ? (
        <>
          <Note variant={TextVariant.BODY_SMALL}>What&apos;s new</Note>
          <Note variant={TextVariant.BODY_SMALL}>{release.notes}</Note>
        </>
      ) : null}
    </>
  )
}

function renderDownload(file: TerminalDownload) {
  const href = downloadUrl(file.path)
  if (!href) return null
  const size = formatSize(file.sizeBytes)
  return (
    <DownloadButton key={file.path} href={href} download>
      <Download size={16} />
      {PLATFORM_LABEL[file.platform]}
      {file.arch ? ` (${file.arch})` : ''}
      {size ? ` · ${size}` : ''}
    </DownloadButton>
  )
}

const Terminal: React.FC = () => {
  const { colors } = useTheme()
  const [state, setState] = useState<ReleaseState>({ status: 'loading' })

  const load = useCallback((signal?: AbortSignal) => {
    setState({ status: 'loading' })
    void fetchRelease(signal).then((next) => {
      if (!signal?.aborted) setState(next)
    })
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  return (
    <MainWrapper>
      <Column>
        <Header>
          <TerminalIcon size={28} color={colors.purple} />
          <StyledTitle variant={TextVariant.H3}>Terminal</StyledTitle>
        </Header>
        <Lead variant={TextVariant.BODY_DEFAULT}>
          The desktop application in which you run your own trading bots and agents, on your own
          machine, with your own keys. One iNODE licence runs one Terminal.
        </Lead>

        <Cards>
          <Card>
            <CardHead>
              <Key size={18} color={colors.cyan} />
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>Your licence is the key</Text>
            </CardHead>
            <Note variant={TextVariant.BODY_SMALL}>
              Terminal signs in with the wallet that holds your iNODE licence and binds to one
              licence. A licence already running elsewhere is released from{' '}
              <InlineLink to="/user-licenses">your licences</InlineLink> first.
            </Note>
          </Card>
          <Card>
            <CardHead>
              <Shield size={18} color={colors.cyan} />
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>Keys stay on your machine</Text>
            </CardHead>
            <Note variant={TextVariant.BODY_SMALL}>
              A bot&apos;s signing key is generated in Terminal and kept by your operating system.
              Nothing on the platform holds it, and nothing here can move your funds.
            </Note>
          </Card>
          <Card>
            <CardHead>
              <TrendingUp size={18} color={colors.cyan} />
              <Text variant={TextVariant.BODY_DEFAULT_BOLD}>Trades through the platform router</Text>
            </CardHead>
            <Note variant={TextVariant.BODY_SMALL}>
              Every trade a bot makes goes through MPRO Lab&apos;s swap router on Base, so its
              volume and fees are visible on chain like every other swap here.
            </Note>
          </Card>
        </Cards>

        <DownloadSection>
          <Flex alignItems="center" gap="8px">
            <Text variant={TextVariant.BODY_DEFAULT_BOLD}>Download</Text>
            <InfoIcon
              iconProps={{ size: 18 }}
              tooltipProps={{
                title:
                  'Terminal is installed on your own computer. It needs an iNODE licence to bind, and a wallet you control to sign in.'
              }}
            />
          </Flex>
          <Downloads state={state} onRetry={() => load()} />
        </DownloadSection>

        {/* What is in it, in nine tiles and nine sentences. */}
        <SectionHead>
          <StyledTitle variant={TextVariant.H3}>What is in it</StyledTitle>
        </SectionHead>
        <Tiles>
          {FEATURES.map((feature) => (
            <Tile key={feature.name}>
              <TileName variant={TextVariant.BODY_DEFAULT_BOLD}>{feature.name}</TileName>
              <Note variant={TextVariant.BODY_SMALL}>{feature.line}</Note>
            </Tile>
          ))}
        </Tiles>

        {/*
          Release notes: version and headline, newest first, and the whole row
          is the link. Five of them, because a page that lists every version
          ever is a changelog and this is a product page — "more" is where the
          changelog lives.
        */}
        <SectionHead>
          <StyledTitle variant={TextVariant.H3}>Release notes</StyledTitle>
          <More to="/terminal/releases">All versions →</More>
        </SectionHead>
        <Table>
          {RELEASE_NOTES.slice(0, 5).map((note) => (
            <Row key={note.version} to={`/terminal/releases#v${note.version}`}>
              <Version variant={TextVariant.BODY_SMALL}>{note.version}</Version>
              <Headline variant={TextVariant.BODY_SMALL}>{note.headline}</Headline>
            </Row>
          ))}
        </Table>
      </Column>
    </MainWrapper>
  )
}

export default Terminal
